"use client";

import { forwardRef, useMemo } from "react";
import { Box, Button, Flex, TextField } from "@radix-ui/themes";
import styles from "./PageIndexPage.module.scss";

const ALL_LETTERS = [
  "#",
  ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
];

type PageIndexSidebarProps = {
  letters: string[];
  activeLetter: string | null;
  onLetterClick: (letter: string) => void;
  disabled: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
};

const PageIndexSidebar = forwardRef<HTMLElement, PageIndexSidebarProps>(
  (
    {
      letters,
      activeLetter,
      onLetterClick,
      disabled,
      searchTerm,
      onSearchChange,
    },
    ref,
  ) => {
    const availableSet = useMemo(() => new Set(letters), [letters]);

    return (
      <Box asChild className={styles.letterFilter}>
        <nav ref={ref} aria-label="Alphabetical navigation">
          <Flex direction="column" gap="2">
            <TextField.Root
              placeholder="Search pages..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              size="2"
            >
              <TextField.Slot>
                <i className="fas fa-search" />
              </TextField.Slot>
            </TextField.Root>
            <Flex asChild gap="1" wrap="nowrap" className={styles.letterList}>
              <ul>
                <li>
                  <Button
                    variant={!activeLetter ? "solid" : "surface"}
                    size="2"
                    onClick={() => onLetterClick("")}
                    disabled={disabled}
                    aria-label="Show all pages"
                    className={styles.letterButton}
                  >
                    All
                  </Button>
                </li>
                {ALL_LETTERS.map((letter) => {
                  const isAvailable = availableSet.has(letter);
                  return (
                    <li key={letter} style={{ listStyle: "none" }}>
                      <Button
                        variant={activeLetter === letter ? "solid" : "surface"}
                        color={!isAvailable ? "gray" : undefined}
                        highContrast={isAvailable}
                        size="2"
                        onClick={() => onLetterClick(letter)}
                        disabled={disabled || !isAvailable}
                        aria-label={`Jump to ${letter === "#" ? "numbers and symbols" : `letter ${letter}`}`}
                        className={styles.letterButton}
                      >
                        {letter}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </Flex>
          </Flex>
        </nav>
      </Box>
    );
  },
);

PageIndexSidebar.displayName = "PageIndexSidebar";

export default PageIndexSidebar;
